import { BackgroundStylesInterface } from 'common/types/background-interfaces'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BackgroundPositionType,
  BackgroundVariantsType,
  backgroundPositionStyles,
  backgroundPositions,
  backgroundPositionValues,
  backgroundVariants,
} from 'src/editor/shared/constants/background-consts'
import {
  OptionSingleValueType,
  SelectNew,
} from 'src/editor/shared/ui-lib/select-new/select-new'
import { BackgroundPositionButton } from './background-position-button'
import {
  detectBackgroundPositionType,
  detectBackgroundType,
  getBackgroundPositionOptions,
} from './utils'

interface BackgroundPositionProps {
  selectTitle: string
  isLecture: boolean
  backgroundPosition?: BackgroundStylesInterface
  onUpdateBackgroundPosition?: (value: BackgroundStylesInterface) => void
}

export const BackgroundPosition = ({
  selectTitle,
  backgroundPosition,
  isLecture,
  onUpdateBackgroundPosition,
}: BackgroundPositionProps) => {
  const { t } = useTranslation()

  const [currentBackgroundType, setCurrentBackgroundType] = useState(() =>
    detectBackgroundType(backgroundPosition, isLecture),
  )
  const [currentPositionType, setCurrentPositionType] = useState(() =>
    detectBackgroundPositionType(backgroundPosition),
  )

  const handleSelectOption = (
    option: OptionSingleValueType<BackgroundVariantsType>,
  ) => {
    if (!option || !onUpdateBackgroundPosition) {
      return
    }

    const newStyle = backgroundPositionStyles[option.value]

    onUpdateBackgroundPosition(newStyle)

    setCurrentBackgroundType(detectBackgroundType(newStyle, isLecture))

    setCurrentPositionType(detectBackgroundPositionType(newStyle))
  }

  const handleClick = (btnType: BackgroundPositionType) => {
    setCurrentPositionType(btnType)
    const updatedPositionValue = {
      ...backgroundPosition,
      backgroundPosition: backgroundPositionValues[btnType],
    }
    if (onUpdateBackgroundPosition) {
      onUpdateBackgroundPosition(updatedPositionValue)
    }
  }

  const backgroundOptions = useMemo(
    () => getBackgroundPositionOptions(isLecture, t),
    [isLecture, t],
  )

  const selectedOption = useMemo(() => {
    return (
      backgroundOptions.find(opt => opt.value === currentBackgroundType) ?? null
    )
  }, [backgroundOptions, currentBackgroundType])

  const isFullWidth = currentBackgroundType === backgroundVariants.Width100
  const isFullSize =
    currentBackgroundType === backgroundVariants.Width100Height100
  const isScaleNoCrop =
    currentBackgroundType === backgroundVariants.ScalesWithoutCropping

  const hideCorners = isFullSize || isFullWidth || isScaleNoCrop

  const buttonPositions = [
    [
      { key: backgroundPositions.TopLeft, isDisabled: hideCorners },
      { key: backgroundPositions.TopCenter, isDisabled: isFullSize },
      { key: backgroundPositions.TopRight, isDisabled: hideCorners },
    ],
    [
      { key: backgroundPositions.CenterLeft, isDisabled: isFullWidth },
      { key: backgroundPositions.CenterCenter, isDisabled: false },
      { key: backgroundPositions.CenterRight, isDisabled: isFullWidth },
    ],
    [
      { key: backgroundPositions.BottomLeft, isDisabled: hideCorners },
      { key: backgroundPositions.BottomCenter, isDisabled: isFullSize },
      { key: backgroundPositions.BottomRight, isDisabled: hideCorners },
    ],
  ]

  useEffect(() => {
    const nextType = detectBackgroundType(backgroundPosition, isLecture)
    setCurrentBackgroundType(nextType)
    setCurrentPositionType(detectBackgroundPositionType(backgroundPosition))
  }, [backgroundPosition])

  return (
    <>
      <SelectNew
        labelText={t(selectTitle)}
        wrapperClassName="m-0"
        size="medium"
        isMulti={false}
        onChange={handleSelectOption}
        options={backgroundOptions}
        selectedOption={selectedOption}
      />
      {currentBackgroundType === backgroundVariants.Fixed && (
        <div className="mt-3 mb-4 flex rounded-md bg-bgWarning p-1.5 text-S text-textPrimary leading-[16px]">
          {t('components.core.settings.background_position.fixed.tooltip')}
        </div>
      )}
      {currentBackgroundType !== backgroundVariants.Fixed && (
        <div className="mt-3 mb-4 flex flex-col gap-2 rounded-md bg-bgSecondary p-3">
          {buttonPositions.map(row => (
            <div
              key={row.map(({ key }) => key).join('-')}
              className="flex items-center justify-between"
            >
              {row.map(({ key, isDisabled }) => (
                <BackgroundPositionButton
                  key={key}
                  isDisabled={isDisabled}
                  isActive={currentPositionType === key}
                  onClick={() => handleClick(key)}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </>
  )
}
